import type { AutopilotHealth, AutopilotStatus } from "@cloudserver/contracts";
import { percent } from "../../../ui/format.js";

/*
 * Small pure helpers of the Autopilot views (tab and dashboard card).
 */

type Tone = "neutral" | "ok" | "warn" | "danger";

export const STATUS_TONE: Record<AutopilotStatus, Tone> = {
  waiting: "neutral",
  connected: "neutral",
  reporting: "ok",
  stale: "danger",
  expired: "warn",
};

export const HEALTH_TONE: Record<AutopilotHealth, Tone> = {
  ok: "ok",
  warn: "warn",
  critical: "danger",
  unknown: "neutral",
};

const DAY = 86_400;
const HOUR = 3600;
const MINUTE = 60;

/** "3 дн. 4 ч", "5 h 12 min": the two largest units of a duration. */
export function durationText(seconds: number, locale: string): string {
  const unit = (value: number, name: "day" | "hour" | "minute") =>
    new Intl.NumberFormat(locale, { style: "unit", unit: name, unitDisplay: "short" }).format(
      value,
    );
  const days = Math.floor(seconds / DAY);
  const hours = Math.floor((seconds % DAY) / HOUR);
  const minutes = Math.floor((seconds % HOUR) / MINUTE);
  if (days > 0)
    return hours > 0 ? `${unit(days, "day")} ${unit(hours, "hour")}` : unit(days, "day");
  if (hours > 0) {
    return minutes > 0 ? `${unit(hours, "hour")} ${unit(minutes, "minute")}` : unit(hours, "hour");
  }
  return unit(minutes, "minute");
}

/** A percentage as the cards show it: whole numbers, one decimal below 10. */
export function percentText(value: number, locale: string): string {
  return percent(value, locale, value < 10 ? 1 : 0);
}

/** Tone of a usage bar: the API's thresholds (memory 90/97, disk 85/95) seen from the panel. */
export function usageTone(value: number, warn: number, critical: number): Tone {
  if (value >= critical) return "danger";
  if (value >= warn) return "warn";
  return "ok";
}
